import PageSection from '../../../Constants&Theme/PageSection'
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material'
import { Navigate, useNavigate } from 'react-router-dom'
import React, { useMemo, useState } from 'react'
import { Person, Phone, Publish, ShoppingCart, Undo } from '@mui/icons-material'
import { useCreateLimsIssue } from '../../../APIs/Hooks/LimsHooks'
import { autoOptionsFromData } from '../../../Functions/ListItemGenerator'
import { useBioBankContext } from '../../../Contexts/Modules/BioBankModule'
import { usePyGet } from '../../../APIs/Hooks/PyAPIHooks'
import { useUserContext } from '../../../Contexts/UserConfig'
import { getLabelFromList } from '../../../Functions/CodeSetFns'
import { stackGap } from '../../../Constants&Theme/Spacing'
import DeliveryDetailsFields from './DeliveryDetailsFields'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { sampleSaleCols } from '../SharedItems/SampleSaleCols'
import { tsSample } from '../../../Typescript/DB_Interface'
import { useQuery } from 'react-query'
import { AxiosResponse } from 'axios'
import PyAPI from '../../../APIs/Python/APIs/PyAPI'
import { useMessagePopupContext } from '../../../garvan-react/Components/ModalFeedback/MessagePopup'
import { useLocalStorage } from 'usehooks-ts'

function SaleCheckout() {
  const navigate = useNavigate()
  const { whoAmI } = useUserContext()
  const { loginConfig } = useUserContext()
  const { setError } = useMessagePopupContext()
  const [cart] = useLocalStorage('BioBankShoppingCart', [])
  const { meQuery } = useUserContext()
  const [fields, setFields] = useState<any>({
    SampleIDs: cart.map((s: any) => s.id),
  })
  const { ownersQuery } = useBioBankContext()
  const projectQuery = usePyGet(['/gia/wd/projects'])
  const facilityQuery = usePyGet(['/facility'])
  const ids = cart.map((s: tsSample) => s.id)
  const priceQuery = useQuery<any, AxiosResponse>(
    ['/lims/sample_prices', fields?.FacilityID, ids],
    () =>
      PyAPI.post(whoAmI, '/lims/sample_prices', {
        FacilityID: fields?.FacilityID,
        SampleIDs: ids,
      }),
    {
      enabled:
        !!whoAmI &&
        !!loginConfig?.Modules?.BioBankCheckoutPrice &&
        !!fields?.FacilityID &&
        cart.length > 0,
      onError: (err) => setError(err),
    },
  )

  const { mutate: createIssue } = useCreateLimsIssue(fields)
  const sampleData = useMemo(() => {
    if (priceQuery.data && loginConfig?.Modules?.BioBankCheckoutPrice) {
      const samples: any[] = []
      cart.forEach((s: tsSample) =>
        samples.push({
          ...s,
          Price: (priceQuery.data as { [key: string]: string }[]).find(
            (p) => p?.[s.id],
          )?.[s.id],
        }),
      )
      return samples
    } else {
      return cart ?? []
    }
  }, [cart, priceQuery.data, loginConfig?.Modules])

  function formChange(e: any) {
    setFields((f: any) => {
      return { ...f, [e.target.name]: e.target.value }
    })
  }

  const validOrder =
    Object.keys(fields).length > 1 &&
    !!fields?.FacilityID &&
    !!fields?.VanRunID &&
    !!fields?.OwnerID &&
    !!fields?.OrderContact &&
    !!fields?.TypeOfResearch &&
    (!fields?.courier || !!fields?.DeliveryAddress) &&
    (!!fields?.PONumber || !!fields?.ProjectID)

  if (cart.length < 1) {
    return (
      <Navigate
        to={'../'}
        replace
      />
    )
  }
  return (
    <Box sx={{ width: 1, maxWidth: 'md' }}>
      <PageSection title={'Delivery Details'}>
        <Stack
          gap={stackGap.col}
          width={1}
        >
          <Stack
            direction={'row'}
            gap={stackGap.row}
            width={1}
          >
            <Stack
              gap={stackGap.col}
              width={1}
            >
              <Autocomplete
                isOptionEqualToValue={(option, value) => {
                  return option.value === value.value
                }}
                onChange={(event: any, newValue: any) => {
                  setFields((f: any) => {
                    return { ...f, FacilityID: newValue?.value ?? undefined }
                  })
                }}
                renderInput={(params) => (
                  <TextField
                    required
                    label={'Delivery Destination'}
                    {...params}
                  />
                )}
                value={
                  {
                    value: fields?.FacilityID,
                    label: getLabelFromList(
                      facilityQuery.data,
                      fields?.FacilityID,
                      'FacilityID',
                      'FacilityName',
                    ),
                  } ?? { id: null, label: 'other' }
                }
                loading={facilityQuery.isFetching}
                fullWidth
                options={autoOptionsFromData(
                  facilityQuery.data,
                  'FacilityID',
                  'FacilityName',
                )}
              />
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
                fullWidth
                required
                name={'OrderContact'}
                label={'Order Contact'}
                onChange={formChange}
                value={fields?.OrderContact ?? ''}
              />
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
                fullWidth
                name={'OrderPhoneNumber'}
                label={'Order Phone Number'}
                onChange={formChange}
                value={fields?.OrderPhoneNumber ?? ''}
              />
              <Autocomplete
                isOptionEqualToValue={(option, value) => {
                  return option.value === value.value
                }}
                onChange={(event: any, newValue: any) => {
                  setFields((f: any) => {
                    return { ...f, OwnerID: newValue?.value ?? undefined }
                  })
                }}
                renderInput={(params) => (
                  <TextField
                    label={'Owner'}
                    {...params}
                    required
                  />
                )}
                loading={ownersQuery.isLoading}
                fullWidth
                options={autoOptionsFromData(
                  ownersQuery.data,
                  'OwnerID',
                  'OwnerName',
                ).sort((a: any, b: any) => {
                  return a.label.localeCompare(b.label)
                })}
              />
              <TextField
                required
                fullWidth
                label={'Type of Research'}
                name={'TypeOfResearch'}
                onChange={formChange}
              />
              {meQuery.data?.profile?.Organisation === 'Garvan' ?
                <Autocomplete
                  onChange={(event: any, newValue: any) => {
                    setFields((f: any) => {
                      return { ...f, ProjectID: newValue?.value ?? undefined }
                    })
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return option.value === value.value
                  }}
                  options={(projectQuery.data ?? []).map((p: any) => {
                    return {
                      value: p.ProjectID,
                      label: `${p.CostCenter}-${p.ProjectID} - ${p.ProjectName}`,
                    }
                  })}
                  renderInput={(params: any) => (
                    <TextField
                      required
                      {...params}
                      fullWidth
                      label="Garvan Project ID"
                    />
                  )}
                />
              : <TextField
                  fullWidth
                  label={'Purchase Order'}
                  name={'PONumber'}
                  onChange={formChange}
                />
              }
            </Stack>
            <DeliveryDetailsFields
              setFields={setFields}
              fields={fields}
            />
          </Stack>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                startIcon={<Undo />}
                endIcon={<ShoppingCart />}
                onClick={() => navigate(-1)}
                variant={'contained'}
              >
                Return to Cart
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                endIcon={<Publish />}
                variant={'contained'}
                color={'success'}
                onClick={() => createIssue()}
                disabled={!validOrder}
              >
                Submit Order
              </Button>
            </Box>
          </Box>
          <PageSection
            title={'Requested Samples'}
            headingLevel={'h2'}
          >
            <DataGridPro
              getRowId={(row) => row?.LIMSSampleId ?? row?.id}
              sx={{ width: 1 }}
              autoHeight
              loading={priceQuery.isLoading}
              disableRowSelectionOnClick
              columns={sampleSaleCols}
              rows={sampleData}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    Storage_Media: false,
                    Freezing_method: false,
                    Method_of_Euthanasia: false,
                    Price: !!loginConfig?.Modules?.BioBankCheckoutPrice,
                  },
                },
              }}
            />
          </PageSection>
        </Stack>
      </PageSection>
    </Box>
  )
}

export default SaleCheckout
